/**
 * Scoring + derivation helpers for the 2×2 matrix and freshness badges.
 * Ported verbatim from local/chris-v1/docs/index.html.
 */
import type {ComplexityCode, Jtbd, MatrixPoint} from './types';

export const H_M_L: Record<string, number> = {High: 3, Medium: 2, Low: 1};
export const FUND_MAP: Record<string, number> = {Funded: 3, Partial: 2, Unfunded: 1};
export const effortSize: Record<string, number> = {High: 22, Medium: 14, Low: 8};

export interface ComplexityMeta {
  label: string;
  bg: string;
  text: string;
  border: string;
}

export const COMPLEXITY_META: Record<ComplexityCode, ComplexityMeta> = {
  L: {label: 'Low — Automation', bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0'},
  M: {label: 'Medium — Mix', bg: '#FFFBEB', text: '#D97706', border: '#FDE68A'},
  H: {label: 'High — Mostly AI', bg: '#FFF1F2', text: '#E11D48', border: '#FECDD3'},
  A: {label: 'Agent', bg: '#F5F3FF', text: '#7C3AED', border: '#DDD6FE'},
  MA: {label: 'Multi-Agent', bg: '#0F172A', text: '#E2E8F0', border: '#334155'}
};

export function complexityMeta(code: string | null | undefined): ComplexityMeta {
  return COMPLEXITY_META[(code as ComplexityCode) ?? 'M'] ?? COMPLEXITY_META.M;
}

/** Build a matrix point from a JTBD's strategy context, or null if it has none. */
export function scToPlot(j: Jtbd, domainName: string, area?: string): MatrixPoint | null {
  const sc = j.strategyContext;
  if (!sc) return null;
  const me = H_M_L[sc.manualEffort ?? ''] || 1;
  const lo = H_M_L[sc.leaderOpportunity ?? ''] || 1;
  const tr = H_M_L[sc.techReadiness ?? ''] || 1;
  const fs = FUND_MAP[sc.fundingStatus ?? ''] || 1;
  return {
    id: `${domainName}__${j.name}`,
    jtbd: j.name,
    domain: domainName,
    area,
    phase: j.phase,
    x: me + lo,
    y: tr + fs,
    hci: sc.humanCapitalImpact,
    complexity: sc.complexity,
    approach: sc.approach,
    manualEffort: sc.manualEffort,
    leaderOpportunity: sc.leaderOpportunity,
    techReadiness: sc.techReadiness,
    fundingStatus: sc.fundingStatus,
    aiVsAutomation: sc.aiVsAutomation,
    synergy: sc.synergy
  };
}

export type Quadrant = 'Act Now' | 'Invest to Unlock' | 'Ready but Low Impact' | 'Monitor';

export function quadrantOf(p: {x: number; y: number}): Quadrant {
  if (p.x > 3 && p.y > 3) return 'Act Now';
  if (p.x > 3 && p.y <= 3) return 'Invest to Unlock';
  if (p.x <= 3 && p.y > 3) return 'Ready but Low Impact';
  return 'Monitor';
}

/** Does a JTBD carry any populated strategy-context value? */
export function hasStrategyContext(j: Jtbd): boolean {
  return !!j.strategyContext && Object.values(j.strategyContext).some(v => v !== null && v !== undefined);
}

/** Normalize a JTBD's `solution` (string | string[] | undefined) to a string array. */
export function solutionList(j: Jtbd): string[] {
  if (Array.isArray(j.solution)) return j.solution.filter(Boolean);
  return j.solution ? [j.solution] : [];
}

/**
 * Splits a prose value into discrete bullet fragments for display (display-only heuristic — no
 * schema/data change). Splits on sentence-ending periods first (period + space + capital letter or
 * digit), then on semicolons within each sentence. Falls back to the original single string if that
 * doesn't yield 2+ fragments, so short or already-atomic values render exactly as before.
 * Ported from the prototype (local/chris-v2/docs/index.html).
 */
export function splitToBullets(text?: string): string[] {
  if (!text || typeof text !== 'string') return [text ?? ''];
  const sentences = text
    .split(/\.\s+(?=[A-Z0-9])/)
    .map(s => s.trim())
    .filter(Boolean);
  const fragments = sentences.flatMap(s =>
    s
      .split(/;\s+/)
      .map(f => f.trim())
      .filter(Boolean)
  );
  const cleaned = fragments.map(f => f.replace(/\.$/, '').trim()).filter(f => f.length > 0);
  return cleaned.length > 1 ? cleaned : [text];
}
